import { useEffect, useState } from "react";
import type { DecaTemplate } from "@deca/shared-types";
import { useCounterparties, useCreateTemplate, useDeleteTemplate, useMe, useTemplatePreview, useTemplates, useUpdateTemplate, useUploadLogo } from "../api/hooks";
import { Button, Card, Chip, ErrorNotice, Field, Input, Modal, Notice, PageHeader, Select, Spinner } from "../components/ui";
import { IconPlus } from "../components/icons";

/**
 * Plantillas de marca (CLAUDE.md Fase 4): logo, colores y pie de página, tanto
 * a nivel de empresa como por cada cargador.
 *
 * La vista previa no es un dibujo de la cabecera: se pide al servidor, que usa
 * **el mismo generador** que emite los documentos reales, con un DeCA de
 * ejemplo que incluye ADR y varios envíos. Lo que se ve aquí es lo que saldrá.
 *
 * Orden de resolución que aplica el sistema, y que conviene tener claro al
 * configurar: plantilla del cargador → plantilla de la empresa → estilo neutro.
 */

interface Draft {
  counterpartyId: string;
  name: string;
  primaryColorHex: string;
  secondaryColorHex: string;
  footerText: string;
  isDefault: boolean;
  logoS3Key: string | null;
}

const EMPTY: Draft = {
  counterpartyId: "",
  name: "",
  primaryColorHex: "#1c438b",
  secondaryColorHex: "#3693f8",
  footerText: "",
  isDefault: false,
  logoS3Key: null,
};

async function fileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export function TemplatesPage() {
  const templates = useTemplates();
  const counterparties = useCounterparties();
  const create = useCreateTemplate();
  const update = useUpdateTemplate();
  const uploadLogo = useUploadLogo();
  const preview = useTemplatePreview();

  const [editing, setEditing] = useState<DecaTemplate | null>(null);
  const [draft, setDraft] = useState<Draft>({ ...EMPTY });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DecaTemplate | null>(null);

  const me = useMe();
  const isAdmin = me.data?.role === "ADMIN";
  const deleteTemplate = useDeleteTemplate();

  const cargadores = (counterparties.data?.items ?? []).filter(
    (item) => item.defaultRole === "CARGADOR_CONTRACTUAL" && item.isActive,
  );

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const selectTemplate = (template: DecaTemplate | null): void => {
    setEditing(template);
    setPreviewUrl(null);
    setDraft(
      template
        ? {
            counterpartyId: template.counterpartyId ?? "",
            name: template.name,
            primaryColorHex: template.primaryColorHex ?? EMPTY.primaryColorHex,
            secondaryColorHex: template.secondaryColorHex ?? EMPTY.secondaryColorHex,
            footerText: template.footerText ?? "",
            isDefault: template.isDefault,
            logoS3Key: template.logoS3Key,
          }
        : { ...EMPTY },
    );
  };

  const payload = (): Record<string, unknown> => ({
    counterpartyId: draft.counterpartyId || null,
    name: draft.name.trim(),
    logoS3Key: draft.logoS3Key,
    primaryColorHex: draft.primaryColorHex,
    secondaryColorHex: draft.secondaryColorHex,
    footerText: draft.footerText.trim() || null,
    isDefault: draft.isDefault,
  });

  const save = (): void => {
    if (editing) update.mutate({ id: editing.id, values: payload() }, { onSuccess: () => setEditing(null) });
    else create.mutate(payload(), { onSuccess: () => setEditing(null) });
  };

  const runPreview = (): void => {
    preview.mutate(payload(), {
      onSuccess: (blob) => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(URL.createObjectURL(blob));
      },
    });
  };

  return (
    <>
      <PageHeader
        eyebrow="Marca"
        title="Plantillas de documentos"
        description="Logo, colores y pie de página que aparecerán en el PDF. La plantilla de la empresa se aplica por defecto; si un cargador tiene la suya, manda la suya."
      />

      <div className="grid gap-4 lg:grid-cols-[1fr_26rem]">
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="micro-label">Plantillas configuradas</h2>
            <Button onClick={() => selectTemplate(null)}>
              <IconPlus className="h-4 w-4" />
              Nueva plantilla
            </Button>
          </div>

          {templates.isLoading ? (
            <Spinner label="Cargando plantillas" />
          ) : templates.isError ? (
            <ErrorNotice error={templates.error} onRetry={() => void templates.refetch()} />
          ) : (templates.data?.items ?? []).length === 0 ? (
            <Notice tone="neutral">
              Todavía no hay ninguna plantilla. Sin plantilla, el documento sale con el estilo neutro
              del sistema: es perfectamente válido, solo que sin su logo ni sus colores.
            </Notice>
          ) : (
            <ul className="space-y-2">
              {(templates.data?.items ?? []).map((template) => {
                const cargador = counterparties.data?.items.find(
                  (item) => item.id === template.counterpartyId,
                );
                return (
                  <li key={template.id} className="surface flex flex-wrap items-center justify-between gap-3 p-3">
                    <div className="flex items-center gap-3">
                      <span
                        aria-hidden="true"
                        className="h-8 w-8 rounded border border-ink-200 dark:border-ink-700"
                        style={{ backgroundColor: template.primaryColorHex ?? "#e5e7eb" }}
                      />
                      <div>
                        <p className="text-sm font-medium text-ink-900 dark:text-ink-50">
                          {template.name}{" "}
                          {template.counterpartyId ? (
                            <Chip tone="neutral">De cargador</Chip>
                          ) : template.isDefault ? (
                            <Chip tone="ok">Por defecto</Chip>
                          ) : null}
                        </p>
                        <p className="text-xs text-ink-500 dark:text-ink-400">
                          {template.counterpartyId
                            ? `Cargador: ${cargador?.name ?? template.counterpartyId}`
                            : "Nivel empresa"}
                          {template.logoS3Key ? " · con logo" : " · sin logo"}
                          {template.footerText ? " · con pie de página" : ""}
                        </p>
                      </div>
                    </div>
                    <Button onClick={() => selectTemplate(template)}>Editar</Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <Card>
          <h2 className="micro-label mb-3">{editing ? `Editar: ${editing.name}` : "Nueva plantilla"}</h2>

          <div className="space-y-3">
            <Field label="Ámbito" htmlFor="t-ambito">
              <Select
                id="t-ambito"
                value={draft.counterpartyId}
                onChange={(event) => setDraft({ ...draft, counterpartyId: event.target.value })}
              >
                <option value="">Toda la empresa (por defecto)</option>
                {cargadores.map((item) => (
                  <option key={item.id} value={item.id}>
                    Solo para {item.name}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Nombre de la plantilla" htmlFor="t-name">
              <Input
                id="t-name"
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Color principal" htmlFor="t-primary">
                <input
                  id="t-primary"
                  type="color"
                  className="h-10 w-full rounded-md border border-ink-300 dark:border-ink-700"
                  value={draft.primaryColorHex}
                  onChange={(event) => setDraft({ ...draft, primaryColorHex: event.target.value })}
                />
              </Field>
              <Field label="Color secundario" htmlFor="t-secondary">
                <input
                  id="t-secondary"
                  type="color"
                  className="h-10 w-full rounded-md border border-ink-300 dark:border-ink-700"
                  value={draft.secondaryColorHex}
                  onChange={(event) => setDraft({ ...draft, secondaryColorHex: event.target.value })}
                />
              </Field>
            </div>

            <Field label="Pie de página" htmlFor="t-footer" hint="Ej.: datos de contacto o número de registro.">
              <Input
                id="t-footer"
                value={draft.footerText}
                onChange={(event) => setDraft({ ...draft, footerText: event.target.value })}
              />
            </Field>

            <Field
              label="Logo"
              htmlFor="t-logo"
              hint="PNG o JPG · hasta 700 KB. El PDF no admite SVG ni WEBP: se rechazan aquí para no fallar más tarde al emitir un documento."
            >
              <input
                id="t-logo"
                type="file"
                accept="image/png,image/jpeg"
                className="field"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  void fileToBase64(file).then((base64) =>
                    uploadLogo.mutate(
                      { filename: file.name, contentType: file.type, base64 },
                      { onSuccess: (result) => setDraft((previous) => ({ ...previous, logoS3Key: result.logoS3Key })) },
                    ),
                  );
                }}
              />
            </Field>
            {draft.logoS3Key ? (
              <p className="ident text-xs text-ink-500 dark:text-ink-400">Logo cargado: {draft.logoS3Key}</p>
            ) : null}
            {uploadLogo.isError ? <ErrorNotice error={uploadLogo.error} /> : null}

            {!draft.counterpartyId ? (
              <label className="flex items-center gap-2 text-sm text-ink-700 dark:text-ink-200">
                <input
                  type="checkbox"
                  checked={draft.isDefault}
                  onChange={(event) => setDraft({ ...draft, isDefault: event.target.checked })}
                />
                Usar como plantilla por defecto de la empresa
              </label>
            ) : null}

            {create.isError || update.isError ? (
              <ErrorNotice error={create.error ?? update.error} />
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                disabled={create.isPending || update.isPending || draft.name.trim().length === 0}
                onClick={save}
              >
                {create.isPending || update.isPending ? "Guardando…" : "Guardar plantilla"}
              </Button>
              <Button onClick={runPreview} disabled={preview.isPending}>
                {preview.isPending ? "Generando…" : "Vista previa"}
              </Button>
                {editing ? <Button onClick={() => selectTemplate(null)}>Nueva</Button> : null}
            </div>

            {isAdmin && editing ? (
              <>
                <hr className="border-ink-100" />
                <div className="space-y-2">
                  <p className="text-sm font-medium text-ink-500">Acciones de administrador</p>
                  <Button
                    variant="quiet"
                    onClick={() => {
                      setDeleteTarget(editing);
                    }}
                  >
                    Eliminar plantilla
                  </Button>
                </div>
              </>
            ) : null}
          </div>

          {preview.isError ? (
            <div className="mt-3">
              <ErrorNotice error={preview.error} />
            </div>
          ) : null}
        </Card>
      </div>

      {/* Modal: confirmar eliminación de plantilla */}
      <Modal
        open={deleteTarget !== null}
        title="Eliminar plantilla"
        onClose={() => setDeleteTarget(null)}
      >
        <div className="space-y-4">
          {deleteTarget?.isDefault ? (
            <Notice tone="warn">
              Esta es la <strong>plantilla por defecto</strong> de la empresa. Al eliminarla, los
              nuevos documentos saldrán con el estilo neutro del sistema hasta que configures otra.
            </Notice>
          ) : (
            <Notice tone="danger">
              Se eliminará permanentemente la plantilla{" "}
              <strong>{deleteTarget?.name}</strong>. Si tiene logo subido, también se borrará de
              almacenamiento. Esta acción <strong>no se puede deshacer</strong>.
            </Notice>
          )}
          {deleteTemplate.isError ? <ErrorNotice error={deleteTemplate.error} /> : null}
          <div className="flex gap-2">
            <Button
              variant="primary"
              disabled={deleteTemplate.isPending}
              onClick={() => {
                if (!deleteTarget) return;
                deleteTemplate.mutate(deleteTarget.id, {
                  onSuccess: () => {
                    setDeleteTarget(null);
                    setEditing(null);
                    setDraft({ ...EMPTY });
                  },
                });
              }}
            >
              {deleteTemplate.isPending ? "Eliminando…" : "Eliminar definitivamente"}
            </Button>
            <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          </div>
        </div>
      </Modal>

      {previewUrl ? (
        <section className="mt-6">
          <h2 className="micro-label mb-2">Vista previa del PDF</h2>
          <p className="mb-3 max-w-3xl text-sm text-ink-600 dark:text-ink-300">
            Generada con el mismo motor que emite los documentos reales y un DeCA de ejemplo con
            mercancía peligrosa y tres envíos.
          </p>
          <iframe
            title="Vista previa de la plantilla"
            src={previewUrl}
            className="h-[38rem] w-full rounded-md border border-ink-200 dark:border-ink-800"
          />
        </section>
      ) : null}
    </>
  );
}
